import { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    GridProps,
    IconButton,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { CheckCircleOutline, RadioButtonUnchecked } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FallbackWaveform from "Images/Content/FallbackWaveform.png";
import giftReceivedIcon from "Images/gifts_received.png";
import moment from "moment";
import { AppButton } from "./AppButton";
import { AppDownloadingIcons } from "./Icon/AppDownloadingIcons";
import { RenderIf } from "./Wrapper/RenderIf";

export type AppContentListItemType =
    | "media"
    | "courses"
    | "purchasableCourses"
    | "gift"
    | "receivedGift";

export interface AppContentListItemProps
    extends InternalStandardProps<GridProps> {
    isAllowClick?: boolean;
    onListItemClick?: () => void;
    onItemPlayClick?: () => void;
    imageUrl: string;
    type?: AppContentListItemType;
    contentPrice?: string;
    isRedeemableByToken?: string;
    numberOfTokens?: string;
    title: string;
    skuId: string;
    description: string;
    showPlayIcon?: boolean;
    showFavIcon?: boolean;
    isFavourite?: boolean;
    categoryId?: string;
    //Course Related Props
    isPurchased?: boolean;
    isResume?: boolean;
    creditsRequired?: number;
    publishFree?: boolean;
    isReceived?: boolean;
    isDownloadInProgress?: boolean;
    isInDownloadQueue?: boolean;
    isDownloaded?: Promise<boolean>;
    inProgressDownloaded?: boolean;
    giftChecked?: boolean;
    onGiftCheckChanged?: (nid: string, checked: boolean) => void;
    selectMode?: boolean;
    purchasedQuantity?: number;
    sortType?: string;
    releaseDate?: string;
    acquiredDate?: string;
    played?: string;
    sender?: {
        first_name?: string;
        last_name?: string;
        sending_date?: string;
    };
    attachedSku?: {
        prospect_sku_id?: string;
        member_sku_id?: string;
    };
    isGiftGiven?: boolean;
    disabled?: boolean;
    nid: string;
    style?: Object;
    isFromPlaylist?: boolean;
    handleToogleCheckBox?: (nid: string, value: boolean) => void;
    isCheckedItem?: boolean;
    isFromSpeakerPlaylist?: boolean;
}

interface StyledCardProps {
    isFromPlaylist?: boolean;
    disabled?: boolean;
    isFromSpeakerPlaylist?: boolean;
}

const StyledCard = styled(Card, {
    shouldForwardProp: (prop) =>
        prop !== "isFromPlaylist" &&
        prop !== "disabled" &&
        prop !== "isFromSpeakerPlaylist",
})<StyledCardProps>(({ theme, isFromPlaylist, disabled, isFromSpeakerPlaylist }) => ({
    backgroundColor: `${theme.palette.background.default} !important`,
    display: "flex",
    alignItems: "flex-start",
    cursor: "pointer !important",
    padding: "10px",
    ...(!isFromPlaylist && {
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
    }),
    ...(disabled && {
        opacity: "50%",
    }),
    ...(isFromSpeakerPlaylist && {
        paddingTop: "10px",
        paddingBottom: "10px",
    }),
}));

interface StyledImageContainerProps {
    isFromSpeakerPlaylist?: boolean;
    hasImageWrapperMargin?: boolean;
}

const StyledImageContainer = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== "isFromSpeakerPlaylist" && prop !== "hasImageWrapperMargin",
})<StyledImageContainerProps>(({ isFromSpeakerPlaylist, hasImageWrapperMargin }) => ({
    width: "80px",
    display: "flex",
    marginTop: "5px",
    justifyContent: "center",
    position: "relative",
    ...(isFromSpeakerPlaylist && {
        justifyContent: "flex-start",
        width: "60px",
        flexShrink: 0,
    }),
    ...(hasImageWrapperMargin && {
        marginRight: "10px",
    }),
}));

const StyledImageWrapper = styled(Box)({
    display: "flex",
    alignItems: "center",
});

const StyledQuantityBox = styled(Box)({
    width: 28,
    height: 28,
    backgroundColor: "#AAAAAA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    flexShrink: 0,
    marginRight: 8,
});

const StyledQuantityText = styled(Typography)({
    fontWeight: 600,
    fontSize: 14,
    color: "#000000", // fixed: box is always #AAAAAA; text.primary is light-blue in dark mode
});

const StyledThumbnail = styled("img")({
    width: "60px",
    height: "auto",
});

interface StyledCardContentProps {
    isShortContent?: boolean;
}

const StyledCardContent = styled(CardContent, {
    shouldForwardProp: (prop) => prop !== "isShortContent",
})<StyledCardContentProps>(({ theme, isShortContent }) => ({
    width: "60%",
    height: "100%",
    padding: "0 10px 0",
    position: "relative",
    display: "flex",
    justifyContent: "start",
    flexDirection: "column",
    [theme.breakpoints.down("md")]: {
        width: "70%",
    },
    [theme.breakpoints.down("sm")]: {
        width: "70%",
    },
    ...(isShortContent && {
        width: "75%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    }),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontSize: "18px",
    marginBottom: "5px",
    color: theme.palette.grey[600],
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const StyledSkuId = styled(Typography)(({ theme }) => ({
    fontSize: "14px",
    color: theme.palette.grey[500],
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
}));

const StyledQuantityOwn = styled(Typography)(({ theme }) => ({
    fontSize: "16px",
    color:
        theme.palette.mode === "light"
            ? theme.palette.grey[900]
            : theme.palette.grey[100],
    width: "100%",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    overflow: "hidden",
    marginTop: "5px",
}));

interface StyledDescriptionProps {
    isSingleLine?: boolean;
}

const StyledDescription = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "isSingleLine",
})<StyledDescriptionProps>(({ theme, isSingleLine }) => ({
    color: theme.palette.grey[600],
    fontSize: "12px",
    ...(isSingleLine
        ? {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
          }
        : {
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
          }),
}));

const StyledActionButtons = styled(Box)({
    display: "flex",
    flexDirection: "column",
    justifyContent: "start",
    height: "100%",
    position: "relative",
    marginTop: "5px",
});

const StyledChip = styled(Chip)(({ theme }) => ({
    width: "70px",
    color: theme.palette.grey[800],
    borderWidth: "2px",
    borderColor: theme.palette.grey[800],
}));

const StyledNumberOfTokens = styled(Typography)(({ theme }) => ({
    marginTop: "20px",
    color: theme.palette.grey[800],
    fontSize: "15px",
    textAlign: "center",
}));

const StyledBtnContentPrice = styled(AppButton)({
    height: "35px",
    paddingTop: "0px",
    paddingBottom: "0px",
    paddingLeft: "8px",
    paddingRight: "8px",
    borderRadius: "16px",
});

const StyledCheckedIcon = styled(CheckCircleOutline)(({ theme }) => ({
    color: theme.palette.grey[700],
}));

const StyledUncheckedIcon = styled(RadioButtonUnchecked)(({ theme }) => ({
    color: theme.palette.grey[700],
}));

const StyledBoldGrey = styled(Typography)(({ theme }) => ({
    fontSize: "12px",
    color: theme.palette.grey[600],
    fontWeight: "bold",
}));

interface StyledFontAwesomeIconProps {
    isResume?: boolean;
}

const StyledGrey600Icon = styled(FontAwesomeIcon, {
    shouldForwardProp: (prop) => prop !== "isResume",
})<StyledFontAwesomeIconProps>(({ theme, isResume }) => ({
    color: isResume ? theme.palette.primary.main : theme.palette.grey[600],
}));

const StyledIconButton = styled(IconButton)({
    padding: 0,
});

export const AppContentListItem = (props: AppContentListItemProps) => {
    const {
        isAllowClick,
        onListItemClick,
        imageUrl,
        title,
        skuId,
        description,
        showPlayIcon,
        showFavIcon,
        isFavourite,
        //Course Related Props
        publishFree,
        isPurchased,
        creditsRequired,
        isReceived,
        contentPrice,
        isRedeemableByToken,
        numberOfTokens,
        isDownloadInProgress,
        isInDownloadQueue,
        isDownloaded,
        inProgressDownloaded,
        type,
        giftChecked,
        onGiftCheckChanged,
        selectMode,
        purchasedQuantity,
        sender,
        sortType,
        releaseDate,
        acquiredDate,
        played,
        attachedSku,
        isResume,
        isGiftGiven = false,
        onItemPlayClick,
        disabled,
        nid,
        style,
        isFromPlaylist,
        handleToogleCheckBox,
        isCheckedItem,
        isFromSpeakerPlaylist,
        categoryId = "",
    } = props;

    const [isMediaDownloaded, setIsMediaDownloaded] = useState<boolean>(false);
    const { handlePlayIconClick } = useHandlers(isAllowClick, onItemPlayClick);

    const toggleGiftChecked = () => onGiftCheckChanged(nid, !giftChecked);
    const showDate = () => {
        if (sortType) {
            if (sortType === "acquired_date") {
                return acquiredDate && acquiredDate !== "0"
                    ? Number.isNaN(+acquiredDate)
                        ? getShortDate(acquiredDate)
                        : `${formatDate(+acquiredDate)}`
                    : "";
            }
            if (sortType === "played") {
                return played && played !== "0"
                    ? `${formatDateTime(+played)}`
                    : "";
            } else if (releaseDate && releaseDate !== "0")
                return `${formatDate(+releaseDate)}`;
            else return "";
        } else if (releaseDate && releaseDate !== "0")
            return `${formatDate(+releaseDate)}`;
        else return "";
    };

    const giftingAvailable = () => {
        if (!attachedSku) return "";
        if (!!attachedSku.prospect_sku_id && !!attachedSku.member_sku_id)
            return "Gift Available (M, P)";
        else if (!!attachedSku.prospect_sku_id) return "Gift Available (P)";
        else if (!!attachedSku.member_sku_id) return "Gift Available (M)";
        return "";
    };

    const getShortDate = (date: string) =>
        moment(date, "MMMM DD, YYYY").format("MMM DD, YYYY");

    const formatDate = (date: number) => moment.unix(date).format("ll");
    const formatDateTime = (date: number) =>
        `${moment.unix(date).format("ll")} @ ${moment
            .unix(date)
            .format("hh:mma")}`;

    useEffect(() => {
        if (!!inProgressDownloaded) setIsMediaDownloaded(true);
        if (isDownloaded !== undefined)
            isDownloaded
                ?.then((res) => {
                    setIsMediaDownloaded(res);
                })
                .catch(() => {
                    setIsMediaDownloaded(false);
                });
        else setIsMediaDownloaded(false);
    }, [isDownloaded, type, inProgressDownloaded, isDownloadInProgress]);

    const onCardClick = () => {
        if (selectMode) toggleGiftChecked();
        else if (isAllowClick) onListItemClick();
    };

    const isCourseType = () =>
        type === "courses" || type === "purchasableCourses";

    return (
        <StyledCard
            onClick={() =>
                isFromSpeakerPlaylist
                    ? handleToogleCheckBox(nid, !isCheckedItem)
                    : onCardClick()
            }
            elevation={0}
            style={style}
            isFromPlaylist={isFromPlaylist}
            disabled={disabled}
            isFromSpeakerPlaylist={isFromSpeakerPlaylist}
        >
            <StyledImageContainer
                isFromSpeakerPlaylist={isFromSpeakerPlaylist}
                hasImageWrapperMargin={categoryId === "send-member-gifts"}
            >
                {categoryId === "send-member-gifts" ? (
                    <StyledImageWrapper>
                        <StyledQuantityBox>
                            <StyledQuantityText>
                                {purchasedQuantity ?? 0}
                            </StyledQuantityText>
                        </StyledQuantityBox>
                        <StyledThumbnail
                            src={imageUrl ? imageUrl : FallbackWaveform}
                            alt="thumbnail"
                        />
                    </StyledImageWrapper>
                ) : (
                    <StyledThumbnail
                        src={imageUrl ? imageUrl : FallbackWaveform}
                        alt="thumbnail"
                    />
                )}
            </StyledImageContainer>
            <StyledCardContent
                isShortContent={type === "purchasableCourses"}
            >
                <StyledTitle>
                    {title || ""}
                </StyledTitle>
                <StyledSkuId>{skuId || ""}</StyledSkuId>
                <StyledSkuId>
                    {isGiftGiven ? `Sent Date: ${showDate()}` : showDate()}
                </StyledSkuId>
                <StyledSkuId>
                    {giftingAvailable()}
                </StyledSkuId>
                <StyledDescription
                    isSingleLine={type === "receivedGift" || type === "purchasableCourses"}
                >
                    {description || ""}
                </StyledDescription>
                {(categoryId === "purchase-gifts-for-members" ||
                    categoryId === "purchase-gifts-for-prospects") && (
                    <StyledQuantityOwn>
                        {`YOU OWN: ${purchasedQuantity || 0}`}
                    </StyledQuantityOwn>
                )}

                {(((type === "courses" || type === "media") && isReceived) ||
                    type === "receivedGift") && (
                    <Box display="flex" sx={{ marginTop: "5px" }}>
                        <Box
                            marginRight={"10px"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <img
                                width={30}
                                height={30}
                                src={giftReceivedIcon}
                                alt={"gift-received-icon"}
                            />
                        </Box>
                        {sender && (
                            <Box>
                                {sender?.sending_date && (
                                    <StyledBoldGrey>
                                        {moment
                                            .unix(+sender?.sending_date)
                                            .format("LL")}
                                    </StyledBoldGrey>
                                )}
                                <StyledBoldGrey>
                                    From: {sender?.first_name || ""}{" "}
                                    {sender?.last_name || ""}
                                </StyledBoldGrey>
                            </Box>
                        )}
                    </Box>
                )}
            </StyledCardContent>

            <StyledActionButtons
                style={{ width: isCourseType() ? "auto" : "25px" }}
            >
                <RenderIf isTrue={type === "purchasableCourses"}>
                    {+contentPrice > 0 && (
                        <StyledChip
                            label={`$${Number.parseFloat(contentPrice).toFixed(2)}`}
                            variant="outlined"
                        />
                    )}
                    {isRedeemableByToken === "1" && (
                        <StyledNumberOfTokens>
                            {numberOfTokens || ""} Credit
                            {+numberOfTokens > 1 ? "s" : ""}
                        </StyledNumberOfTokens>
                    )}
                </RenderIf>
                <RenderIf
                    isTrue={
                        type === "courses" &&
                        !publishFree &&
                        !isReceived &&
                        !isPurchased
                    }
                >
                    <StyledBtnContentPrice
                        rounded
                        buttonVariant="grey-outlined"
                    >
                        {`$ ${Number.parseFloat(contentPrice).toFixed(2)}`}
                    </StyledBtnContentPrice>

                    {isRedeemableByToken === "1" && (
                        <StyledBtnContentPrice
                            buttonVariant="grey-text"
                        >
                            {creditsRequired} Credit
                            {+creditsRequired > 1 && "s"}
                        </StyledBtnContentPrice>
                    )}
                </RenderIf>
                <RenderIf isTrue={type === "media" || type === "receivedGift"}>
                    {showFavIcon && (
                        <StyledGrey600Icon
                            style={{
                                fontSize: "22px",
                                marginBottom: "15px",
                            }}
                            icon={[isFavourite ? "fas" : "far", "star"]}
                        />
                    )}
                    {showPlayIcon && (
                        <StyledIconButton
                            onClick={handlePlayIconClick}
                        >
                            <StyledGrey600Icon
                                isResume={isResume}
                                style={{
                                    fontSize: "22px",
                                    marginBottom: "15px",
                                    visibility: showPlayIcon
                                        ? "visible"
                                        : "hidden",
                                }}
                                icon={["fas", "play"]}
                            />
                        </StyledIconButton>
                    )}
                </RenderIf>
                <>
                    {isDownloadInProgress && (
                        <AppDownloadingIcons iconType="in-progress" />
                    )}
                    {isInDownloadQueue && (
                        <AppDownloadingIcons iconType="queued" />
                    )}
                    {isMediaDownloaded && (
                        <StyledGrey600Icon
                            style={{
                                fontSize: "22px",
                            }}
                            icon={["far", "floppy-disk"]}
                        />
                    )}
                </>
                {(selectMode || isFromSpeakerPlaylist) && (
                    <Checkbox
                        onClick={(e) =>
                            isFromSpeakerPlaylist &&
                            handleToogleCheckBox(nid, !isCheckedItem)
                        }
                        checked={
                            isFromSpeakerPlaylist ? isCheckedItem : giftChecked
                        }
                        icon={<StyledUncheckedIcon />}
                        checkedIcon={<StyledCheckedIcon />}
                    />
                )}
            </StyledActionButtons>
        </StyledCard>
    );
};

const useHandlers = (isAllowClick: boolean, onItemPlayClick?: () => void) => {
    const handlePlayIconClick = (e) => {
        if (isAllowClick && onItemPlayClick) {
            e.stopPropagation();
            onItemPlayClick();
        }
    };
    return { handlePlayIconClick };
};
